/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Route, Switch } from "react-router-dom";
import {
  StyledEngineProvider,
  Theme,
  ThemeProvider,
} from "@mui/material/styles";
import {
  Message,
  isIFrame,
  MessageActions,
  useDomHistoryMonitor,
  PortalMessageService,
  ThemeSwitchRequestMessage,
  ThemeSwitchResponseMessage,
} from "@jda/lui-portal-utilities";
import { DarkTheme, LightTheme } from "@jda/lui-common-component-library-mui5";

import Customer from "./containers/customer";
import Organization from "./containers/organization";
import { Breadcrumbs } from "./shared/breadcrumbs/Breadcrumbs";
import { CustomAppBar } from "./shared/CustomAppBar/CustomAppBar";
import { selectTheme } from "./store/theme/themeSelector";
import { getUserInfo } from "./services/customer";
import { getAuthDetails } from "./auth/getAuthDetails";
import { getAuthDetailsAction } from './store/auth/authActionCreator';
import { selectAllAssignedPermissions } from './store/auth/authSelector';
import { checkAuthzPermission } from "./components/checkPermission/CheckViewPermission";
import { actions, resources } from "./components/checkPermission/constants";
import Loader from "./shared/loader/Loader";
import SnackbarAlert from "./shared/snackbar/SnackbarAlert";
import { selectSnackBarMessage } from "./store/snackBar/snackBarSelector";

const themes: any = { dark: DarkTheme, light: LightTheme };

const App = () => {
  const dispatch = useDispatch();
  const currentTheme = useSelector(selectTheme);
  const snackBarMessage = useSelector(selectSnackBarMessage);

  const [theme, setTheme] = useState<Theme>(DarkTheme);
  const [userInfo, setUserInfo] = useState<any>(null);
  const assignedPermissions = useSelector(selectAllAssignedPermissions);

  const orgView = assignedPermissions &&
    assignedPermissions.resources &&
    checkAuthzPermission(actions.VIEW, assignedPermissions.resources[resources.ORGANIZATION]);
  const customerView = assignedPermissions &&
    assignedPermissions.resources &&
    checkAuthzPermission(actions.VIEW, assignedPermissions.resources[resources.CUSTOMER]);

  useDomHistoryMonitor();

  const portalEventsHandler = (message: Message) => {
    if (message.action === MessageActions.ThemeSwitchResponse) {
      setTheme(themes[(message as ThemeSwitchResponseMessage).theme]);
    }
  };

  useEffect(() => {
    setTheme(currentTheme === "light" ? LightTheme : DarkTheme);
  }, [currentTheme]);

  const getProfileInfo = async () => {
    const userInfo = await getUserInfo();
    if (userInfo) setUserInfo(userInfo);
    getAuthDetails(dispatch)
  };

  useEffect(() => {
    if (isIFrame()) {
      PortalMessageService.getInstance().registerListener(portalEventsHandler);
      const themeSwitchRequestMessage = new ThemeSwitchRequestMessage();
      PortalMessageService.getInstance().sendMessageToPortal(
        themeSwitchRequestMessage
      );
    }
    getProfileInfo();
    return () => {
      dispatch(getAuthDetailsAction({}));
    }
  }, []);

  useDomHistoryMonitor();
  const isLoading = !userInfo || !assignedPermissions;
  return (
    <div className="app" data-testid="app">
      <ThemeProvider theme={theme}>
        <StyledEngineProvider injectFirst>
          <Breadcrumbs />
          {/* <Typography variant="h2" style={{padding: 32, position: "fixed"}} >{"Management Console"}</Typography> */}
          {!isIFrame() && <CustomAppBar accountInfo={userInfo} />}
          <Loader showLoader={isLoading} isCenter />
          <Switch>
            {customerView && <Route component={Customer} exact path="/" />}
            {customerView && <Route component={Customer} exact path="/customer" />}
            {!customerView && orgView && <Route component={Organization} exact path="/" />}
            {!customerView && orgView && <Route component={Organization} exact path="/organization" />}

            {customerView && orgView && <Route component={Organization} exact path="/organization" />}
          </Switch>
        </StyledEngineProvider>
        {snackBarMessage.show && (
          <SnackbarAlert
            show={snackBarMessage.show}
            message={snackBarMessage.message}
            severity={snackBarMessage.severity}
            autoHideDuration={snackBarMessage.autoHideDuration}
          />
        )}
      </ThemeProvider>
    </div>
  );
};

export default App;